import { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, AlertTriangle, ShieldCheck, Lock, ChevronRight, Fingerprint, CheckCircle2 } from 'lucide-react';
import UmbrellaLogo from '@/components/UmbrellaLogo';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { MISSION_BRIEFING } from '@/config/missionContent';

const OBJECTIVES = MISSION_BRIEFING.objectives;

const TERMS = MISSION_BRIEFING.terms;

const SYS_MESSAGES = MISSION_BRIEFING.sysMessages;

const DECRYPT_PATHS = MISSION_BRIEFING.decryptPaths;

// ─── TypedLine — used sparingly for dramatic moments only ───
function TypedLine({ text, speed = 30, delay = 0, className = '', onDone }) {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    if (displayed.length >= text.length) {
      setDone(true);
      onDone?.();
      return;
    }
    const t = setTimeout(() => setDisplayed(text.slice(0, displayed.length + 1)), speed);
    return () => clearTimeout(t);
  }, [started, displayed, text, speed, onDone]);

  if (!started) return null;
  return (
    <span className={className}>
      {displayed}
      {!done && <span className="animate-pulse text-green-400">▌</span>}
    </span>
  );
}

// ─── Quick Decrypt Opening (~2.5s) ───
function DecryptSequence({ onDone }) {
  const [progress, setProgress] = useState(0);
  const [currentPath, setCurrentPath] = useState(0);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          setComplete(true);
          setTimeout(onDone, 500);
          return 100;
        }
        return p + 2 + Math.random() * 2.5;
      });
    }, 40);
    const pathInterval = setInterval(() => {
      setCurrentPath(p => (p + 1) % DECRYPT_PATHS.length);
    }, 350);
    return () => { clearInterval(interval); clearInterval(pathInterval); };
  }, [onDone]);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      animate={complete ? { opacity: 0 } : { opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="absolute inset-0 bg-black" />

      {/* Scanlines */}
      <div className="absolute inset-0 pointer-events-none z-10 opacity-[0.06]"
        style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,65,0.4) 2px, rgba(0,255,65,0.4) 4px)' }} />

      <div className="relative z-20 flex flex-col items-center w-80">
        {/* Umbrella logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="mb-8"
        >
          <UmbrellaLogo size={64} />
        </motion.div>

        {/* Status text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-terminal text-xs tracking-[4px] text-green-500 uppercase mb-4"
        >
          {complete ? MISSION_BRIEFING.decrypt.complete : MISSION_BRIEFING.decrypt.decrypting}
        </motion.p>

        {/* Progress bar */}
        <div className="w-full h-2 bg-green-950/40 border border-green-800/30 rounded overflow-hidden mb-3">
          <div
            className="h-full rounded transition-all duration-75"
            style={{
              width: `${Math.min(100, progress)}%`,
              background: complete
                ? 'linear-gradient(90deg, #00cc33, #00ff41)'
                : 'linear-gradient(90deg, #0a6e2e, #00cc33)',
              boxShadow: complete ? '0 0 12px rgba(0,255,65,0.5)' : '0 0 6px rgba(0,255,65,0.3)',
            }}
          />
        </div>

        {/* Percentage + file path */}
        <div className="w-full flex justify-between items-center">
          <p className="font-terminal text-xs text-green-800 truncate flex-1">
            {'> '}{DECRYPT_PATHS[currentPath]}
          </p>
          <span className="font-terminal text-xs text-green-400 ml-2">
            {Math.min(100, Math.floor(progress))}%
          </span>
        </div>

        {/* Green flash on complete */}
        {complete && (
          <motion.div
            className="fixed inset-0 bg-green-500/8 pointer-events-none"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </div>
    </motion.div>
  );
}

// ─── Floating Particles (reduced, with memoized trajectories) ───
function FloatingParticles() {
  const particles = useMemo(() =>
    Array.from({ length: 20 }, (_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: i < 3 ? 3 : 1 + Math.random() * 1.5,
      duration: 10 + Math.random() * 10,
      delay: Math.random() * 5,
      drift: (Math.random() - 0.5) * 16,
      isRed: i < 3,
    })), []);

  return (
    <div className="fixed inset-0 pointer-events-none z-0">
      {particles.map((p, i) => (
        <motion.div
          key={i}
          className={`absolute rounded-full ${p.isRed ? 'bg-red-500/25' : 'bg-green-500/15'}`}
          style={{
            width: p.size,
            height: p.size,
            left: `${p.x}%`,
            top: `${p.y}%`,
            boxShadow: p.isRed ? '0 0 6px rgba(204,0,0,0.2)' : undefined,
          }}
          animate={{
            y: [0, -25, 0],
            x: [0, p.drift, 0],
            opacity: [0.2, 0.6, 0.2],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

// ─── Background Radar (CSS animation) ───
function BackgroundRadar() {
  return (
    <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0 opacity-[0.025]">
      <svg width="500" height="500" viewBox="0 0 500 500" className="animate-spin-slow">
        <circle cx="250" cy="250" r="200" fill="none" stroke="#00ff41" strokeWidth="0.5" />
        <circle cx="250" cy="250" r="140" fill="none" stroke="#00ff41" strokeWidth="0.3" />
        <circle cx="250" cy="250" r="80" fill="none" stroke="#00ff41" strokeWidth="0.3" />
        <line x1="250" y1="50" x2="250" y2="450" stroke="#00ff41" strokeWidth="0.2" />
        <line x1="50" y1="250" x2="450" y2="250" stroke="#00ff41" strokeWidth="0.2" />
        <line x1="250" y1="250" x2="250" y2="50" stroke="#00ff41" strokeWidth="1.5" opacity="0.4" />
      </svg>
    </div>
  );
}

// ─── Scanlines (CSS only) ───
function Scanlines() {
  return (
    <div className="fixed inset-0 pointer-events-none z-40 opacity-[0.03]"
      style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,65,0.4) 2px, rgba(0,255,65,0.4) 4px)' }} />
  );
}

// ─── Red Vignette (CSS only) ───
function RedVignette() {
  return (
    <div className="fixed inset-0 pointer-events-none z-0 animate-pulse"
      style={{
        animationDuration: '5s',
        boxShadow: 'inset 0 0 100px rgba(120,0,0,0.12), inset 0 0 30px rgba(120,0,0,0.06)',
      }}
    />
  );
}

// ─── HUD Corners ───
function HUDCorners() {
  return (
    <>
      {[
        'top-2 left-2 border-t-2 border-l-2',
        'top-2 right-2 border-t-2 border-r-2',
        'bottom-2 left-2 border-b-2 border-l-2',
        'bottom-2 right-2 border-b-2 border-r-2',
      ].map((cls, i) => (
        <div key={i} className={`fixed ${cls} w-6 h-6 border-green-600/15 pointer-events-none z-30`} />
      ))}
    </>
  );
}

// ─── Top Bar ───
function TopBar() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const update = () => setTime(new Date().toLocaleTimeString('en-US', { hour12: false }));
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed top-0 left-0 right-0 z-30 h-7 bg-black/70 border-b border-green-800/20 flex items-center px-4 backdrop-blur-sm"
    >
      <span className="font-terminal text-xs tracking-[3px] text-green-700/80 uppercase">
        {MISSION_BRIEFING.topBar.label}
      </span>
      <div className="ml-auto flex items-center gap-4">
        <span className="font-terminal text-xs text-green-800/60">{MISSION_BRIEFING.topBar.docId}</span>
        <span className="font-terminal text-xs text-green-600/80">{time}</span>
        <div className="flex gap-0.5 items-end">
          {[3, 5, 7, 9].map((h, i) => (
            <div key={i} className="w-1 bg-green-500/50 rounded-full" style={{ height: h }} />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Bottom Ticker ───
function BottomTicker() {
  const tickerText = SYS_MESSAGES.join('  ◆  ');
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.5 }}
      className="fixed bottom-0 left-0 right-0 z-30 h-6 bg-black/70 border-t border-green-800/20 overflow-hidden flex items-center backdrop-blur-sm"
    >
      <motion.p
        className="font-terminal text-xs text-green-800/60 whitespace-nowrap"
        animate={{ x: ['100%', '-100%'] }}
        transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
      >
        {tickerText}  ◆  {tickerText}
      </motion.p>
    </motion.div>
  );
}

// ─── Classified Stamp (fast slam with impact) ───
function ClassifiedStamp({ visible, onSlam }) {
  const splatters = useMemo(() =>
    Array.from({ length: 5 }, () => ({
      x: (Math.random() - 0.5) * 100,
      y: (Math.random() - 0.5) * 50,
      size: 2 + Math.random() * 4,
    })), []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ scale: 3.5, rotate: -25, opacity: 0 }}
          animate={{ scale: 1, rotate: -12, opacity: 0.15 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onAnimationComplete={onSlam}
          className="absolute top-6 right-6 pointer-events-none z-20 select-none"
        >
          {/* Impact rings */}
          {[0, 1, 2].map(i => (
            <motion.div
              key={i}
              className="absolute inset-0 flex items-center justify-center"
              initial={{ scale: 0.3, opacity: 0.7 }}
              animate={{ scale: 2.5 + i, opacity: 0 }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
            >
              <div className="w-28 h-14 rounded-full border border-red-500/30" />
            </motion.div>
          ))}

          {/* Ink splatters */}
          {splatters.map((s, i) => (
            <motion.div
              key={`s${i}`}
              className="absolute top-1/2 left-1/2 rounded-full bg-red-600/25"
              style={{ width: s.size, height: s.size }}
              initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
              animate={{ x: s.x, y: s.y, opacity: [0, 0.5, 0.2], scale: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            />
          ))}

          <div className="border-4 border-red-600 rounded px-6 py-2">
            <span className="font-terminal text-3xl tracking-[8px] text-red-600 uppercase">{MISSION_BRIEFING.stamp || 'Classified'}</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Accepted Stamp ───
function AcceptedStamp() {
  return (
    <motion.div
      initial={{ scale: 4, rotate: -20, opacity: 0 }}
      animate={{ scale: 1, rotate: -8, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 18 }}
      className="absolute inset-0 flex items-center justify-center pointer-events-none z-30"
    >
      {/* Green flash */}
      <motion.div
        className="absolute inset-0 bg-green-500/10"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      />
      {/* Impact rings */}
      {[0, 1, 2].map(i => (
        <motion.div
          key={i}
          className="absolute border border-green-500/25 rounded-lg"
          style={{ width: 260 + i * 50, height: 90 + i * 18 }}
          initial={{ scale: 0.5, opacity: 0.5 }}
          animate={{ scale: 1.5, opacity: 0 }}
          transition={{ duration: 0.7, delay: i * 0.08 }}
        />
      ))}
      <div className="border-[5px] border-green-500/80 rounded-lg px-10 py-4 bg-green-950/30 backdrop-blur-sm"
        style={{ boxShadow: '0 0 40px rgba(0,255,65,0.2), inset 0 0 20px rgba(0,255,65,0.05)' }}>
        <p className="font-terminal text-3xl md:text-4xl tracking-[10px] text-green-400 text-glow-green uppercase">
          {MISSION_BRIEFING.acceptedStamp}
        </p>
      </div>
    </motion.div>
  );
}

// ─── Operative Name — Letter-by-letter reveal ───
function OperativeName({ name, delay = 0 }) {
  const [revealed, setRevealed] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => {
    if (!started || revealed >= name.length) return;
    const t = setTimeout(() => setRevealed(r => r + 1), 80);
    return () => clearTimeout(t);
  }, [started, revealed, name.length]);

  return (
    <span className="inline-flex">
      {name.split('').map((char, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 4 }}
          animate={i < revealed ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.15 }}
          className="text-red-400 font-terminal tracking-wider text-sm"
          style={i < revealed ? {
            textShadow: '0 0 8px rgba(204,0,0,0.5), 0 0 16px rgba(204,0,0,0.2)',
          } : undefined}
        >
          {char}
        </motion.span>
      ))}
      {started && revealed < name.length && (
        <span className="animate-pulse text-red-400 ml-px">▌</span>
      )}
    </span>
  );
}

// ─── Section Divider Line ───
function SectionDivider({ delay = 0 }) {
  return (
    <motion.div
      className="h-px w-full overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay }}
    >
      <motion.div
        className="h-full"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,65,0.15), transparent)' }}
        initial={{ x: '-100%' }}
        animate={{ x: '0%' }}
        transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      />
    </motion.div>
  );
}

// ─── Holographic Shimmer ───
function HolographicShimmer() {
  return (
    <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden rounded">
      <motion.div
        className="absolute left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,65,0.12), transparent)' }}
        animate={{ top: ['-5%', '105%'] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
      />
      <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-green-500/4 to-transparent" />
      <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-red-500/4 to-transparent" />
    </div>
  );
}

// ─── Main Component ───
export default function MissionBriefing({ onComplete }) {
  const [showDecrypt, setShowDecrypt] = useState(true);
  const [showLeon, setShowLeon] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [showStamp, setShowStamp] = useState(false);
  const [showAcceptStamp, setShowAcceptStamp] = useState(false);
  const [sealGlow, setSealGlow] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const [objectivesChecked, setObjectivesChecked] = useState(false);
  const [showAuthText, setShowAuthText] = useState(false);
  const [phase, setPhase] = useState(0); // 0=nothing, 1=header, 2=details, 3=objectives, 4=terms, 5=footer
  const [stampFlash, setStampFlash] = useState(false);

  // Mount-reset
  useEffect(() => {
    setShowDecrypt(true);
    setShowLeon(false);
    setAccepted(false);
    setShowContent(false);
    setShowStamp(false);
    setShowAcceptStamp(false);
    setSealGlow(false);
    setScreenShake(false);
    setObjectivesChecked(false);
    setShowAuthText(false);
    setPhase(0);
    setStampFlash(false);
  }, []);

  const handleDecryptDone = useCallback(() => {
    setShowDecrypt(false);
    setShowLeon(true);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowContent(true);
    // Orchestrated section reveals — no overlapping
    setTimeout(() => setPhase(1), 200);          // header
    setTimeout(() => setShowStamp(true), 700);    // stamp slams
    setTimeout(() => setPhase(2), 1200);          // details grid
    setTimeout(() => setPhase(3), 2400);          // objectives
    setTimeout(() => setPhase(4), 3600);          // terms
    setTimeout(() => setPhase(5), 5200);          // footer + button
  }, []);

  const handleStampSlam = useCallback(() => {
    setScreenShake(true);
    setStampFlash(true);
    setTimeout(() => setScreenShake(false), 250);
    setTimeout(() => setStampFlash(false), 120);
  }, []);

  const handleAccept = useCallback(() => {
    setAccepted(true);
    setSealGlow(true);
    setObjectivesChecked(true);
    setTimeout(() => setShowAcceptStamp(true), 500);
    setTimeout(() => setShowAuthText(true), 1100);
    setTimeout(onComplete, 2800);
  }, [onComplete]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="mission-briefing">
      {/* Background layers */}
      <div className="fixed inset-0 z-0 bg-gradient-to-b from-black via-[#020805] to-black" />
      <div className="fixed inset-0 z-0 opacity-[0.025]"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,255,65,1) 0px, transparent 1px, transparent 60px), repeating-linear-gradient(90deg, rgba(0,255,65,1) 0px, transparent 1px, transparent 60px)' }} />

      <FloatingParticles />
      <BackgroundRadar />
      <RedVignette />
      <Scanlines />

      {/* Stamp flash overlay */}
      <AnimatePresence>
        {stampFlash && (
          <motion.div
            className="fixed inset-0 z-50 bg-red-500/8 pointer-events-none"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
          />
        )}
      </AnimatePresence>

      {/* Screen shake wrapper */}
      <motion.div
        className="w-full flex flex-col items-center justify-center"
        animate={screenShake ? { x: [0, -3, 3, -2, 2, 0], y: [0, 2, -2, 1, 0] } : {}}
        transition={{ duration: 0.25 }}
      >
        {/* Decrypt opening */}
        {showDecrypt && <DecryptSequence onDone={handleDecryptDone} />}

        {/* HUD elements */}
        {showContent && (
          <>
            <TopBar />
            <BottomTicker />
            <HUDCorners />
          </>
        )}

        {/* Main document */}
        <AnimatePresence>
          {showContent && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="max-w-2xl w-full relative z-10"
            >
              <div className="relative terminal-panel overflow-hidden">
                <HolographicShimmer />
                <ClassifiedStamp visible={showStamp} onSlam={handleStampSlam} />
                {showAcceptStamp && <AcceptedStamp />}

                {/* Terminal Header Bar */}
                <div className="relative h-9 flex items-center px-4 text-xs font-terminal tracking-wider">
                  <div className="flex items-center gap-1.5 mr-3">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" style={{ boxShadow: '0 0 4px rgba(255,0,0,0.4)' }} />
                    <div className="w-2 h-2 rounded-full bg-yellow-500/30" />
                    <div className="w-2 h-2 rounded-full bg-green-500/30" />
                  </div>
                  <FileText size={11} className="text-green-600/80 mr-1.5" />
                  <span className="text-green-600/80">{MISSION_BRIEFING.terminalHeader}</span>
                  <span className="ml-auto text-green-800/50">{MISSION_BRIEFING.docId}</span>
                </div>

                <div className={`p-6 md:p-8 space-y-6 transition-all duration-500 ${accepted ? 'opacity-30 blur-[2px]' : ''}`}>

                  {/* ── Section 1: Document Header ── */}
                  {phase >= 1 && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                      <div className="flex items-center gap-5 pb-5">
                        <div className="relative flex-shrink-0">
                          <motion.div
                            animate={sealGlow ? {
                              boxShadow: ['0 0 0 rgba(0,255,65,0)', '0 0 30px rgba(0,255,65,0.3)', '0 0 0 rgba(0,255,65,0)'],
                            } : {}}
                            transition={{ duration: 1 }}
                            className="rounded-full"
                          >
                            <UmbrellaLogo size={56} />
                          </motion.div>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                            className="absolute -inset-1.5 border border-dashed border-red-800/15 rounded-full pointer-events-none"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h1 className="font-terminal text-lg md:text-xl tracking-[5px] text-red-500 uppercase text-glow-red">
                            <TypedLine text={MISSION_BRIEFING.header.title} speed={35} delay={100} />
                          </h1>
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                            className="text-xs tracking-[3px] text-green-700/80 uppercase font-terminal mt-0.5"
                          >
                            {MISSION_BRIEFING.header.subtitle}
                          </motion.p>
                        </div>
                        <motion.div
                          animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="flex flex-col items-center gap-1"
                        >
                          <AlertTriangle size={18} className="text-yellow-600" />
                          <span className="text-xs font-terminal text-yellow-700/80 tracking-wider">{MISSION_BRIEFING.header.alert}</span>
                        </motion.div>
                      </div>
                      <SectionDivider delay={0.3} />
                    </motion.div>
                  )}

                  {/* ── Section 2: Mission Details Grid ── */}
                  {phase >= 2 && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        {MISSION_BRIEFING.details.map((item, i) => (
                          <motion.div
                            key={item.label}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.15, duration: 0.4 }}
                            className="flex items-start gap-2"
                          >
                            <span className="text-green-800/30 font-terminal text-xs mt-0.5">{item.icon}</span>
                            <div>
                              <span className="text-xs font-terminal tracking-[3px] text-green-800/80 uppercase block leading-tight">{item.label}</span>
                              {item.isOperative ? (
                                <span className="font-terminal tracking-wider text-sm">
                                  <span className="text-red-400/60">AGENT </span>
                                  <OperativeName name={CONFIG.recipientName.toUpperCase()} delay={300 + i * 150} />
                                </span>
                              ) : (
                                <motion.span
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 0.2 + i * 0.15 }}
                                  className={`font-terminal tracking-wider text-sm ${item.color}`}
                                >
                                  {item.value}
                                </motion.span>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* ── Section 3: Mission Objectives ── */}
                  {phase >= 3 && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                      <SectionDivider />
                      <div className="pt-4">
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4 }}
                          className="flex items-center gap-2 mb-4"
                        >
                          <motion.div
                            className="w-1 h-4 bg-green-500/60 rounded-full"
                            animate={{ opacity: [0.4, 1, 0.4] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          />
                          <h2 className="font-terminal text-sm tracking-[4px] text-green-500 uppercase text-glow-green">
                            {MISSION_BRIEFING.objectivesHeading}
                          </h2>
                        </motion.div>
                        <div className="space-y-2.5">
                          {OBJECTIVES.map((obj, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -12 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.2 + i * 0.15, duration: 0.4 }}
                              className="flex items-start gap-3"
                            >
                              {/* Checkbox */}
                              <div className="w-6 h-6 rounded border border-green-800/30 bg-green-950/20 flex items-center justify-center flex-shrink-0 mt-0.5 relative overflow-hidden">
                                <AnimatePresence mode="wait">
                                  {objectivesChecked ? (
                                    <motion.div
                                      key="check"
                                      initial={{ scale: 0, rotate: -180 }}
                                      animate={{ scale: 1, rotate: 0 }}
                                      transition={{ type: 'spring', stiffness: 400, damping: 15, delay: i * 0.1 }}
                                    >
                                      <CheckCircle2 size={14} className="text-green-400" style={{ filter: 'drop-shadow(0 0 4px rgba(0,255,65,0.5))' }} />
                                    </motion.div>
                                  ) : (
                                    <span className="text-green-600/50 font-terminal text-xs">
                                      {String(i + 1).padStart(2, '0')}
                                    </span>
                                  )}
                                </AnimatePresence>
                                {objectivesChecked && (
                                  <motion.div
                                    className="absolute inset-0 bg-green-500/20"
                                    initial={{ opacity: 1 }}
                                    animate={{ opacity: 0 }}
                                    transition={{ duration: 0.3, delay: i * 0.1 }}
                                  />
                                )}
                              </div>
                              <span className="text-gray-400 font-rajdhani text-sm leading-relaxed">{obj}</span>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* ── Section 4: Terms ── */}
                  {phase >= 4 && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                      <SectionDivider />
                      <div className="pt-4">
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4 }}
                          className="flex items-center gap-2 mb-4"
                        >
                          <div className="w-1 h-4 bg-red-500/40 rounded-full" />
                          <h2 className="font-terminal text-sm tracking-[4px] text-green-500 uppercase text-glow-green">
                            {MISSION_BRIEFING.termsHeading}
                          </h2>
                        </motion.div>
                        <div className="space-y-2 pl-2 border-l border-green-900/10">
                          {TERMS.map((term, i) => (
                            <motion.p
                              key={i}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.15 + i * 0.2, duration: 0.4 }}
                              className={`font-special text-sm ${term.color} leading-relaxed`}
                            >
                              <span className="text-green-800/60 mr-1">-</span>
                              {term.special ? (
                                <motion.span
                                  animate={{ textShadow: ['0 0 0px transparent', '0 0 8px rgba(236,72,153,0.35)', '0 0 0px transparent'] }}
                                  transition={{ duration: 2.5, repeat: Infinity }}
                                >
                                  {term.text}
                                </motion.span>
                              ) : term.text}
                            </motion.p>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* ── Section 5: Footer / Accept ── */}
                  {phase >= 5 && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                      <SectionDivider />
                      <div className="pt-5">
                        <p className="text-xs text-green-900/60 font-terminal tracking-wider text-center mb-5">
                          {MISSION_BRIEFING.penalty}
                        </p>

                        {!accepted && (
                          <div className="text-center">
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.2 }}
                              className="mb-4"
                            >
                              <div className="flex items-center justify-center gap-2 mb-2">
                                <Fingerprint size={14} className="text-red-600/80" />
                                <span className="text-xs font-terminal tracking-[3px] text-red-700/80 uppercase">
                                  {MISSION_BRIEFING.biometricAuth}
                                </span>
                                <Fingerprint size={14} className="text-red-600/80" />
                              </div>
                              <div className="flex items-center justify-center gap-2">
                                <Lock size={10} className="text-red-700/60" />
                                <span className="text-xs font-terminal tracking-[3px] text-red-700/60 uppercase">
                                  {MISSION_BRIEFING.signatureRequired}
                                </span>
                                <Lock size={10} className="text-red-700/60" />
                              </div>
                            </motion.div>

                            <motion.button
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.4 }}
                              whileHover={{ scale: 1.02, boxShadow: '0 0 25px rgba(204,0,0,0.25)' }}
                              whileTap={{ scale: 0.97 }}
                              onClick={handleAccept}
                              className="group relative px-10 py-3.5 bg-gradient-to-r from-red-950/50 via-red-900/40 to-red-950/50 border-2 border-red-600/50 text-red-300 font-terminal text-lg tracking-[6px] uppercase transition-all duration-300 hover:border-red-500/70 overflow-hidden"
                              data-testid="accept-mission-btn"
                            >
                              {/* Border pulse */}
                              <motion.div
                                className="absolute inset-0 pointer-events-none"
                                style={{ boxShadow: 'inset 0 0 0 2px rgba(204,0,0,0)' }}
                                animate={{ boxShadow: ['inset 0 0 0 2px rgba(204,0,0,0)', 'inset 0 0 0 2px rgba(204,0,0,0.3)', 'inset 0 0 0 2px rgba(204,0,0,0)'] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              />
                              {/* Shimmer sweep */}
                              <motion.div
                                className="absolute inset-0 bg-gradient-to-r from-transparent via-red-500/8 to-transparent pointer-events-none"
                                animate={{ x: ['-200%', '200%'] }}
                                transition={{ duration: 3, repeat: Infinity, repeatDelay: 1.5 }}
                              />
                              <span className="relative z-10 flex items-center justify-center gap-3">
                                <ShieldCheck size={18} />
                                {MISSION_BRIEFING.acceptButton}
                                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                              </span>
                            </motion.button>
                          </div>
                        )}

                        {/* Auth text after accept */}
                        {showAuthText && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center mt-4"
                          >
                            <TypedLine
                              text={MISSION_BRIEFING.authText}
                              speed={25}
                              className="font-terminal text-xs tracking-[4px] text-green-400 text-glow-green"
                            />
                          </motion.div>
                        )}

                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6 }}
                          className="text-xs text-gray-700/80 mt-5 font-special italic text-center"
                        >
                          {MISSION_BRIEFING.footerQuote}
                        </motion.p>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Leon dialogue */}
      {showLeon && (
        <LeonDialogue
          message={MISSION_BRIEFING.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}
    </div>
  );
}
